import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalShellContext from './PortalShellContext';
import ThemePicker from './ThemePicker';
import NotificationBell from './NotificationBell';
import { usePortalTheme } from './usePortalTheme';
import { useChromeThemeColor } from './useChromeThemeColor';
import { usePageCanvasColor } from './usePageCanvasColor';
import logoShort from '../../assets/LOGOshort.png';
import './PortalLayout.css';
// Comfort-theme scoped button/focus overrides (additive; no-op at Default).
import '../../styles/themes/comfort-portal.css';
import '../../styles/portal/portalSpaces.css';
import GuideTour from './Guide/GuideTour';

/* Shared chrome for the Phase B multi-space surfaces (hub + the four space
   shells). Provides the .portal-shell wrapper — so it inherits the portal-scoped
   Comfort data-theme system — plus the sticky top bar (theme picker, app-mode
   badge, user menu). It deliberately omits the employee tools sidebar: these are
   the space-switcher surfaces, not the admin tools shell (PortalLayout).

   `leading` is the top-bar's left content (the space-switcher for owners, or a
   neutral CYV brand lockup for the hub / single-space dashboard). */
function PortalSpaceChrome({ leading, children }) {
	const { currentUser, userRole, logout } = useAuth();
	const navigate = useNavigate();
	const { selection, setSelection, appliedTheme } = usePortalTheme();
	const [isStandalone, setIsStandalone] = useState(false);
	// Keep the installed-PWA titlebar on the same colour as the chrome bar.
	const shellRef = useRef(null);
	useChromeThemeColor(shellRef, appliedTheme);
	usePageCanvasColor(shellRef, appliedTheme);

	useEffect(() => {
		const standalone =
			window.matchMedia('(display-mode: standalone)').matches ||
			window.navigator.standalone === true;
		setIsStandalone(standalone);
	}, []);

	const handleLogout = async () => {
		try {
			await logout();
			navigate('/portal/login');
		} catch (error) {
			console.error('Logout error:', error);
		}
	};

	const roleLabel =
		userRole === 'admin'
			? 'Admin'
			: userRole === 'employee'
			? 'Employee'
			: 'Customer';

	const userMenu = (
		<Dropdown align='end'>
			<Dropdown.Toggle variant='outline-secondary' id='portal-user-dropdown'>
				<i className='fa-sharp fa-solid fa-user' />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Header>
					<small>{currentUser?.email}</small>
					<div>
						<small className='badge bg-primary mt-1'>{roleLabel}</small>
					</div>
				</Dropdown.Header>
				<Dropdown.Divider />
				<Dropdown.Item onClick={handleLogout}>
					<i className='fa-sharp fa-solid fa-right-from-bracket me-2' />
					Log Out
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);

	return (
		<PortalShellContext.Provider value={true}>
			<div
				ref={shellRef}
				className='cyv portal-shell'
				data-theme={appliedTheme || undefined}>
				<div className='portal-main'>
					<header className='portal-topbar'>
						<div className='psp-topbar__leading'>{leading}</div>
						<div className='portal-topbar__spacer' />
						<div className='portal-topbar__actions'>
							<ThemePicker
								value={selection}
								onChange={setSelection}
							/>
							{isStandalone && (
								<span className='portal-appmode-badge'>
									<i className='fa-sharp fa-solid fa-mobile-screen' />
									App Mode
								</span>
							)}
							<NotificationBell />
							{userMenu}
						</div>
					</header>

					<main className='portal-content psp-content'>{children}</main>
				</div>
				{/* Renders nothing outside a GuideProvider (only WorkShell has one). */}
				<GuideTour />
			</div>
		</PortalShellContext.Provider>
	);
}

// A neutral CYV brand lockup for the hub bar + the single-space dashboard bar.
// Carries no space identity by design. Defaults to /portal (the hub) for an
// owner; for a single-space user /portal just redirects back to their
// dashboard, so it's a harmless "home". `to` lets a shell point the logo at its
// own home directly (the customer shell → /portal/customer).
export function PortalBrandLockup({ to = '/portal' }) {
	return (
		<Link to={to} className='psp-brand' aria-label='Color Your Voice'>
			<img className='psp-brand__logo' src={logoShort} alt='' />
			<span className='psp-brand__name'>Color Your Voice</span>
		</Link>
	);
}

export default PortalSpaceChrome;
